import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Landmark, MapPin, CalendarClock, ExternalLink, Search, Loader2, ShieldCheck, Info, Building2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SEOHead } from '../components/SEOHead';
import { TenderAlertsPanel } from '../components/tenders/TenderAlertsPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALL = '__all__';

const daysLeft = (deadline) => {
  if (!deadline) return null;
  return Math.ceil((new Date(deadline) - new Date()) / 86400000);
};

const TenderCard = ({ t }) => {
  const d = daysLeft(t.deadline);
  return (
    <Card className="card-surface hover:shadow-md transition-shadow" data-testid={`tender-card-${t.publication_number}`}>
      <CardContent className="p-5">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <Badge className="badge-trade" data-testid="tender-trade">{t.trade_label}</Badge>
          <Badge variant="outline" className="gap-1"><MapPin className="w-3 h-3" /> {t.region}</Badge>
          {t.accreditation && <Badge className="bg-[#1E3A8A]/10 text-[#1E3A8A] gap-1" data-testid="tender-accreditation"><ShieldCheck className="w-3 h-3" /> Agréation : {t.accreditation}</Badge>}
          {d !== null && d >= 0 && d <= 7 && <Badge className="badge-urgent">J-{d}</Badge>}
        </div>
        <h3 className="font-heading font-bold text-[#111827] leading-snug" data-testid="tender-title">{t.title}</h3>
        <p className="text-sm text-[#6B7280] mt-1 flex items-center gap-1.5"><Building2 className="w-4 h-4" /> {t.buyer}{t.city ? ` · ${t.city}` : ''}</p>
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-3 border-t border-[#E5E7EB]">
          <div className="text-sm text-[#4B5563] flex items-center gap-1.5">
            <CalendarClock className="w-4 h-4 text-[#F59E0B]" />
            {t.deadline ? <>Date limite : <strong>{new Date(t.deadline).toLocaleDateString('fr-FR')}</strong></> : 'Date limite : voir annonce'}
            <span className="text-[#9CA3AF]">· publié le {t.published_at ? new Date(t.published_at).toLocaleDateString('fr-FR') : '—'}</span>
          </div>
          <a href={t.official_url} target="_blank" rel="noopener noreferrer" data-testid="tender-official-link">
            <Button size="sm" variant="outline" className="gap-1.5 border-[#1E3A8A]/30 text-[#1E3A8A]"><ExternalLink className="w-4 h-4" /> Voir l'annonce officielle</Button>
          </a>
        </div>
      </CardContent>
    </Card>
  );
};

export default function PublicTendersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [trade, setTrade] = useState(ALL);
  const [region, setRegion] = useState(ALL);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);

  const loadMeta = useCallback(async () => {
    try { setMeta((await axios.get(`${API}/tenders/meta`, { withCredentials: true })).data); } catch { /* silent */ }
  }, []);

  useEffect(() => { loadMeta(); }, [loadMeta]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const params = { page, limit: 20 };
    if (trade !== ALL) params.trade = trade;
    if (region !== ALL) params.region = region;
    if (q.trim()) params.q = q.trim();
    axios.get(`${API}/tenders`, { params }).then((res) => { if (active) setData(res.data); }).catch(() => {}).finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [trade, region, q, page]);

  // Payment return
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (searchParams.get('cancelled')) { toast.info('Paiement annulé'); setSearchParams({}, { replace: true }); return; }
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await axios.get(`${API}/tenders/checkout-status/${sessionId}`, { withCredentials: true });
        if (res.data.payment_status === 'paid') {
          toast.success('Abonnement Alertes Marchés Publics activé ! Configurez vos filtres ci-dessous.', { duration: 8000 });
          setSearchParams({}, { replace: true });
          loadMeta();
          return;
        }
        if (res.data.status === 'expired') { toast.error('Session de paiement expirée'); setSearchParams({}, { replace: true }); return; }
      } catch { /* retry */ }
      if (attempts < 8) setTimeout(poll, 2500);
      else { toast.info('Paiement en cours de confirmation : votre accès sera activé automatiquement dans un délai maximum de 15 minutes.', { duration: 10000 }); setSearchParams({}, { replace: true }); }
    };
    poll();
  }, [searchParams, setSearchParams, loadMeta]);

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <SEOHead title="Chantiers publics – Marchés publics du bâtiment en Belgique" description="Appels d'offres et marchés publics du secteur de la construction en Belgique : gros œuvre, toiture, électricité, HVAC… Filtrez par métier et province." path="/marches-publics" />
      <section className="bg-[#1E3A8A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p className="text-overline text-blue-200 mb-2">Belgique · Source officielle TED</p>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl mb-3" data-testid="tenders-title">Chantiers publics</h1>
          <p className="text-blue-100 max-w-2xl">Les appels d'offres du bâtiment publiés par les pouvoirs publics belges, résumés et filtrables par métier et province. {meta?.total ? <strong className="text-white">{meta.total} marchés actifs</strong> : null}{meta?.last_sync && <span className="text-blue-200 text-sm"> · mis à jour le {new Date(meta.last_sync).toLocaleDateString('fr-FR')}</span>}</p>
        </div>
      </section>

      <div className="bg-[#F59E0B] text-[#111827]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 text-sm font-medium flex items-center gap-2" data-testid="official-site-banner">
          <Info className="w-4 h-4 flex-shrink-0" /> Pour candidater à ce marché, rendez-vous sur le site officiel. BuildConnect ne publie qu'un résumé et ne gère pas les candidatures aux marchés publics.
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          <Card className="card-surface"><CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <Select value={trade} onValueChange={(v) => { setTrade(v); setPage(1); }}>
              <SelectTrigger data-testid="tender-filter-trade"><SelectValue placeholder="Métier" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Tous les métiers</SelectItem>
                {(meta?.trades || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.label} ({t.count})</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={region} onValueChange={(v) => { setRegion(v); setPage(1); }}>
              <SelectTrigger data-testid="tender-filter-region"><SelectValue placeholder="Province" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Toutes les provinces</SelectItem>
                {(meta?.regions || []).map((r) => <SelectItem key={r.id} value={r.id}>{r.label} ({r.count})</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
              <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Rechercher (titre, pouvoir adjudicateur)" className="pl-9 input-field" data-testid="tender-search" />
            </div>
          </CardContent></Card>

          <p className="text-sm text-[#6B7280]" data-testid="tenders-count">{data.total} marché{data.total > 1 ? 's' : ''} trouvé{data.total > 1 ? 's' : ''}</p>

          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" /></div>
          ) : data.items.length === 0 ? (
            <Card className="p-10 text-center text-[#6B7280]" data-testid="tenders-empty"><Landmark className="w-10 h-10 mx-auto mb-3 text-[#9CA3AF]" />Aucun marché ne correspond à ces filtres.</Card>
          ) : (
            <div className="space-y-3" data-testid="tenders-list">{data.items.map((t) => <TenderCard key={t.id} t={t} />)}</div>
          )}

          {data.pages > 1 && (
            <div className="flex items-center justify-center gap-3 pt-4">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="tenders-prev">Précédent</Button>
              <span className="text-sm text-[#6B7280]">Page {page} / {data.pages}</span>
              <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="tenders-next">Suivant</Button>
            </div>
          )}
          <p className="text-xs text-[#9CA3AF] pt-4">Données issues de TED (Tenders Electronic Daily, Union européenne) — publication officielle des marchés publics belges au-dessus des seuils européens. Seul un résumé structuré est affiché ; le texte intégral et la participation se font sur le site officiel.</p>
        </div>

        <aside className="space-y-6 lg:sticky lg:top-24 self-start">
          <TenderAlertsPanel meta={meta} onChanged={loadMeta} />
        </aside>
      </div>
    </div>
  );
}
